import { createFileRoute, redirect } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { api, apiFetch, ApiError } from '@/lib/api';
import { getKioskSession } from '@/lib/session';
import { enqueue, getQueue } from '@/lib/offlineQueue';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/kiosk')({
  beforeLoad: () => {
    if (!getKioskSession()) throw redirect({ to: '/kiosk/activate', search: { secret: '' } });
  },
  component: KioskDashboard,
});

type KioskZone = {
  id: string;
  characterId: string;
  name: string;
  colorState: 'green' | 'amber' | 'red' | 'neutral';
  lastCompletedAt: string | null;
  archivedAt: string | null;
};

type KioskEquipment = { id: string; name: string; archivedAt: string | null };

type KioskDashboardData = {
  property: { id: string; name: string; archivedAt: string | null };
  zones: KioskZone[];
  equipment: KioskEquipment[];
};

const ZONE_COLOR: Record<KioskZone['colorState'], string> = {
  green: 'border-green-400 bg-green-50 dark:bg-green-950',
  amber: 'border-amber-400 bg-amber-50 dark:bg-amber-950',
  red: 'border-red-400 bg-red-50 dark:bg-red-950',
  neutral: 'border-border bg-card',
};

function ZoneCard({ zone, onLog }: { zone: KioskZone; onLog: () => void }) {
  return (
    <button
      onClick={onLog}
      className={cn(
        'rounded-xl border-2 p-5 text-left transition-colors hover:brightness-95 active:scale-95',
        ZONE_COLOR[zone.colorState],
      )}
    >
      <div className="font-mono text-xs text-muted-foreground mb-1">{zone.characterId}</div>
      <div className="font-semibold text-base leading-tight mb-2">{zone.name}</div>
      <div className="text-xs text-muted-foreground">
        {zone.lastCompletedAt
          ? new Date(zone.lastCompletedAt).toLocaleDateString()
          : 'Never logged'}
      </div>
    </button>
  );
}

type KioskLogModalProps = {
  zones: KioskZone[];
  equipment: KioskEquipment[];
  preselectedZoneId: string | null;
  onClose: () => void;
  onSuccess: () => void;
};

function KioskLogModal({ zones, equipment, preselectedZoneId, onClose, onSuccess }: KioskLogModalProps) {
  const { data: members } = useQuery({
    queryKey: ['kiosk-members'],
    queryFn: () => api.get<{ userId: string; displayName: string }[]>('/v1/kiosk/property-members'),
  });

  const [workedOn, setWorkedOn] = useState(() => new Date().toISOString().slice(0, 10));
  const [equipmentId, setEquipmentId] = useState('');
  const [attributedUserId, setAttributedUserId] = useState('');
  const [selectedZoneIds, setSelectedZoneIds] = useState<Set<string>>(
    () => new Set(preselectedZoneId ? [preselectedZoneId] : []),
  );
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit() {
    if (submitting || !equipmentId || selectedZoneIds.size === 0 || !attributedUserId) return;
    setSubmitting(true);
    setError(null);
    const payload = {
      zoneIds: [...selectedZoneIds],
      equipmentId,
      workedOn: new Date(workedOn).toISOString(),
      attributedUserId,
    };
    try {
      await apiFetch<unknown>('/v1/kiosk/zone-completions', {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': crypto.randomUUID(),
        },
      });
      onSuccess();
      onClose();
    } catch (err) {
      if (!navigator.onLine || !(err instanceof ApiError)) {
        enqueue(payload);
        onSuccess();
        onClose();
      } else {
        setError(err.message ?? 'Something went wrong.');
        setSubmitting(false);
      }
    }
  }

  const activeEquipment = equipment.filter((e) => !e.archivedAt);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-background rounded-lg border border-border shadow-lg w-full max-w-md mx-4 p-6 flex flex-col gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Log completion</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-xl leading-none">×</button>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">Who did the work?</label>
          <select
            value={attributedUserId}
            onChange={(e) => setAttributedUserId(e.target.value)}
            className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="">Select person…</option>
            {members?.map((m) => (
              <option key={m.userId} value={m.userId}>{m.displayName}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">Date</label>
          <input
            type="date"
            value={workedOn}
            onChange={(e) => setWorkedOn(e.target.value)}
            className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">Equipment</label>
          <select
            value={equipmentId}
            onChange={(e) => setEquipmentId(e.target.value)}
            className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="">Select equipment…</option>
            {activeEquipment.map((eq) => (
              <option key={eq.id} value={eq.id}>{eq.name}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">Zones</label>
          <div className="flex flex-col gap-1 max-h-48 overflow-y-auto rounded-md border border-input p-2">
            {zones.map((zone) => (
              <label key={zone.id} className="flex items-center gap-2 cursor-pointer text-sm py-0.5">
                <input
                  type="checkbox"
                  checked={selectedZoneIds.has(zone.id)}
                  onChange={(e) => {
                    setSelectedZoneIds((prev) => {
                      const next = new Set(prev);
                      e.target.checked ? next.add(zone.id) : next.delete(zone.id);
                      return next;
                    });
                  }}
                />
                <span className="font-mono text-xs bg-muted px-1 rounded text-muted-foreground">{zone.characterId}</span>
                <span>{zone.name}</span>
              </label>
            ))}
          </div>
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => void handleSubmit()}
            disabled={!attributedUserId || !equipmentId || selectedZoneIds.size === 0 || submitting}
          >
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function KioskDashboard() {
  const session = getKioskSession()!;
  const [logOpen, setLogOpen] = useState(false);
  const [preselectedZone, setPreselectedZone] = useState<string | null>(null);
  const [queueCount, setQueueCount] = useState(() => getQueue().length);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['kiosk-dashboard'],
    queryFn: () => api.get<KioskDashboardData>('/v1/kiosk/dashboard'),
    refetchInterval: 60_000,
  });

  useEffect(() => {
    function onOnline() { setQueueCount(getQueue().length); }
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, []);

  if (isPending) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-background">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <p className="text-destructive">Failed to load dashboard.</p>
          <Button onClick={() => void refetch()}>Retry</Button>
        </div>
      </div>
    );
  }

  const activeZones = data.zones.filter((z) => !z.archivedAt);
  const activeEquipment = data.equipment.filter((e) => !e.archivedAt);

  function handleSuccess() {
    setQueueCount(getQueue().length);
    void refetch();
  }

  return (
    <div className="fixed inset-0 flex flex-col bg-background overflow-hidden">
      <header className="flex items-center justify-between px-6 py-3 border-b border-border shrink-0">
        <div>
          <span className="font-bold text-lg">{data.property.name}</span>
          <span className="ml-3 text-sm text-muted-foreground">{session.deviceLabel}</span>
        </div>
        <div className="flex items-center gap-3">
          {queueCount > 0 && (
            <span className="text-xs text-amber-600">{queueCount} pending sync</span>
          )}
          <Button size="sm" onClick={() => { setPreselectedZone(null); setLogOpen(true); }}>
            Log completion
          </Button>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto p-6">
        {activeZones.length === 0 ? (
          <p className="text-muted-foreground">No zones configured for this property.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {activeZones.map((zone) => (
              <ZoneCard
                key={zone.id}
                zone={zone}
                onLog={() => { setPreselectedZone(zone.id); setLogOpen(true); }}
              />
            ))}
          </div>
        )}
      </main>

      {logOpen && (
        <KioskLogModal
          zones={activeZones}
          equipment={activeEquipment}
          preselectedZoneId={preselectedZone}
          onClose={() => { setLogOpen(false); setPreselectedZone(null); }}
          onSuccess={handleSuccess}
        />
      )}
    </div>
  );
}
